import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CalendarClock, Download, FileText, Loader2, Stethoscope, User } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";
import { downloadCsv } from "@/lib/exportCsv";

const doctorLabel = (doctor) => {
  const composed = [doctor.title, doctor.initials, doctor.surname].filter(Boolean).join(" ").trim();
  return (doctor.doctor_name || "").trim() || composed || doctor.doctor_id;
};

const EVENT_STYLES = {
  visit: { dot: "bg-blue-500", label: "Visit" },
  referral: { dot: "bg-emerald-500", label: "Referral" },
  pad_issued: { dot: "bg-violet-500", label: "Pad issued" },
};

const countByDate = (events, type, key) => {
  const map = {};
  events
    .filter((event) => event.type === type)
    .forEach((event) => {
      const date = event.date || "Unknown";
      map[date] = (map[date] || 0) + 1;
    });
  return Object.entries(map)
    .map(([date, count]) => ({ date, [key]: count }))
    .sort((a, b) => a.date.localeCompare(b.date));
};

export default function DoctorTimelinePage() {
  const [doctors, setDoctors] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/doctors`, { withCredentials: true })
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setDoctors(list);
        // Deep link from the dashboard: /doctor-timeline?doctor=DOC-123
        const wanted = new URLSearchParams(window.location.search).get("doctor");
        const match = wanted && list.find((doctor) => doctor.doctor_id === wanted);
        if (match) pickDoctor(match);
      })
      .catch((error) => {
        console.error("Doctor list load failed", error);
        toast.error("Failed to load doctors");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const matches = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [];
    return doctors
      .filter((doctor) => {
        const haystack = [doctorLabel(doctor), doctor.mp_number, doctor.practice_name, doctor.area]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(term);
      })
      .slice(0, 8);
  }, [doctors, query]);

  const loadTimeline = useCallback(async (doctor) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/reports/doctor-timeline/${encodeURIComponent(doctor.doctor_id)}`, {
        withCredentials: true,
      });
      setTimeline(data);
    } catch (error) {
      console.error("Timeline load failed", error);
      toast.error("Failed to load doctor timeline");
    } finally {
      setLoading(false);
    }
  }, []);

  const pickDoctor = (doctor) => {
    setSelected(doctor);
    setQuery(doctorLabel(doctor));
    loadTimeline(doctor);
  };

  const events = timeline?.timeline || [];
  const visitData = countByDate(events, "visit", "visits");
  const referralData = countByDate(events, "referral", "referrals");

  const exportTimeline = () => {
    if (!timeline) return;
    downloadCsv(
      `doctor-timeline-${timeline.doctor_id}`,
      ["Date", "Event", "Detail"],
      events.map((event) => {
        const data = event.data || {};
        const detail =
          event.type === "visit"
            ? `${data.visit_type || "Visit"} by ${data.rep_name || "—"}`
            : event.type === "referral"
            ? `${data.name || ""} ${data.surname || ""} (TIN ${data.tin_number || "—"}) · ${data.current_stage || ""}`
            : `${data.total_referrals || 0} slips issued by ${data.issued_by_rep || "—"}`;
        return [event.date, EVENT_STYLES[event.type]?.label || event.type, detail.trim()];
      })
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="doctor-timeline-page">
      <div className="px-6 pt-6 pb-4">
        <h1 className="text-2xl font-bold tracking-tight">Doctor Timeline</h1>
        <p className="text-sm text-muted-foreground">Visits, referrals and referral pads for one doctor</p>
      </div>

      <div className="bg-white border-y px-6 py-4">
        <div className="max-w-xl relative">
          <Label className="text-xs">Find a doctor</Label>
          <Input
            placeholder="Start typing a doctor name, MP number or practice"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(null);
            }}
            className="mt-1"
            data-testid="doctor-search-input"
          />
          {!selected && matches.length > 0 && (
            <div
              className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg overflow-hidden"
              data-testid="doctor-search-results"
            >
              {matches.map((doctor) => (
                <button
                  key={doctor.doctor_id}
                  onClick={() => pickDoctor(doctor)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                  data-testid={`doctor-option-${doctor.doctor_id}`}
                >
                  <p className="text-sm font-medium">{doctorLabel(doctor)}</p>
                  <p className="text-xs text-muted-foreground">
                    {[doctor.mp_number, doctor.practice_name, doctor.area].filter(Boolean).join(" · ") || "No practice details"}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24" data-testid="timeline-loading">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : !timeline ? (
        <div className="px-6 py-24 text-center text-muted-foreground" data-testid="timeline-empty">
          <Stethoscope className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">Search for a doctor above to see their visit and referral history</p>
        </div>
      ) : (
        <div className="px-6 py-6 space-y-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold" data-testid="timeline-doctor-name">{timeline.doctor_name}</h2>
              <p className="text-xs text-muted-foreground">{timeline.doctor_id}</p>
            </div>
            <Button variant="outline" size="sm" className="gap-2" onClick={exportTimeline} data-testid="timeline-export-btn">
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: "Total visits", value: timeline.summary.total_visits, icon: User, tone: "text-blue-600", testId: "timeline-kpi-visits" },
              { label: "Total referrals", value: timeline.summary.total_referrals, icon: FileText, tone: "text-emerald-600", testId: "timeline-kpi-referrals" },
              { label: "Pads issued", value: timeline.summary.pads_issued, icon: CalendarClock, tone: "text-violet-600", testId: "timeline-kpi-pads" },
            ].map((item) => (
              <Card key={item.label} data-testid={item.testId}>
                <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                  <CardTitle className="text-sm font-medium text-muted-foreground">{item.label}</CardTitle>
                  <item.icon className={`h-4 w-4 ${item.tone}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{item.value}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card data-testid="visit-frequency-chart">
              <CardHeader>
                <CardTitle className="text-base">Visit frequency</CardTitle>
                <CardDescription>Visits logged per date</CardDescription>
              </CardHeader>
              <CardContent className="h-72">
                {visitData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={visitData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="visits" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground">No visits recorded for this doctor.</p>
                )}
              </CardContent>
            </Card>

            <Card data-testid="referral-frequency-chart">
              <CardHeader>
                <CardTitle className="text-base">Referral frequency</CardTitle>
                <CardDescription>Referrals received per date</CardDescription>
              </CardHeader>
              <CardContent className="h-72">
                {referralData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={referralData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Line type="monotone" dataKey="referrals" stroke="#059669" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground">No referrals recorded for this doctor.</p>
                )}
              </CardContent>
            </Card>
          </div>

          <Card data-testid="timeline-events-card">
            <CardHeader>
              <CardTitle className="text-base">Complete timeline</CardTitle>
              <CardDescription>{events.length} events in chronological order</CardDescription>
            </CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nothing recorded for this doctor yet.</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                  {events.map((event, index) => {
                    const style = EVENT_STYLES[event.type] || { dot: "bg-gray-400", label: event.type };
                    const info = event.data || {};
                    return (
                      <div
                        key={`${event.type}-${event.date}-${index}`}
                        className="flex gap-3 bg-gray-50 rounded-lg px-3 py-2"
                        data-testid={`timeline-event-${index}`}
                      >
                        <span className={`mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 ${style.dot}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-semibold">{style.label}</p>
                            <Badge variant="secondary" className="text-xs">{event.date || "—"}</Badge>
                          </div>
                          {event.type === "visit" && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {info.visit_type || "Visit"} by {info.rep_name || "—"}
                              {info.area ? ` · ${info.area}` : ""}
                            </p>
                          )}
                          {event.type === "referral" && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {info.name} {info.surname} · TIN {info.tin_number || "—"} · {info.current_stage || "—"}
                            </p>
                          )}
                          {event.type === "pad_issued" && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {info.total_referrals || 0} slips issued by {info.issued_by_rep || "—"} · {info.referrals_remaining ?? 0} left
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
